<template>
  <v-dialog
    fullscreen
    v-model="isOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    transition="slide-y-reverse-transition"
    aria-labelledby="spot-selection-title">
    <v-card class="dialog-list">
      <v-card-title id="spot-selection-title" class="dialog-list__title">
        <h2 class="typography-heading-sm-bold">Move asset to:</h2>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="closeDialog"
          :aria-label="t('global.basicTerms.close')">
          <v-icon icon="mdi-close"></v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0 d-flex flex-column flex-1 min-h-0 overflow-hidden">
        <v-skeleton-loader v-if="isFetching" type="list-item-three-line" />

        <div v-else class="d-flex flex-column gap-1 flex-1 min-h-0 overflow-hidden">
          <div class="d-flex flex-column justify-start gap-1 px-5 flex-shrink-0">
            <v-text-field
              hide-details
              v-model="search"
              :placeholder="t('global.basicTerms.search')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              color="primary"
              class="flex-grow-1" />
            <v-checkbox
              v-model="showDisabled"
              label="Show disabled"
              density="compact"
              hide-details
              class="flex-shrink-0 typography-body-md text--color-text-secondary" />
          </div>
          <div class="flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
            <div
              v-if="hasNoSpots"
              class="d-flex flex-column align-center justify-center pa-8 flex-1">
              <p class="typography-body-md text--color-text-secondary text-center">
                No spots available
              </p>
            </div>
            <v-list v-else class="dialog-list__list">
              <template v-for="(item, index) in spotsByArea" :key="index">
                <v-list-subheader
                  v-if="isHeaderItem(item)"
                  :title="item.header"
                  class="typography-caption-bold mt-5"></v-list-subheader>

                <v-list-item
                  v-else
                  :disabled="item.disabled"
                  variant="plain"
                  color="#C96500"
                  class="dialog-list__item"
                  :class="{ disabled: item.disabled }"
                  @click="handleSpotClick(item)">
                  <template #prepend>
                    <v-icon size="x-small" :icon="item.icon"></v-icon>
                  </template>

                  <template #title>
                    <div class="d-flex flex-column justify-center">
                      <span class="typography-body-md">{{ item.code }}</span>
                      <span class="typography-caption">{{ item.group }}</span>
                    </div>
                  </template>

                  <template #append>
                    <span class="typography-body-sm text-capitalize text--color-text-secondary">
                      {{ item.status }}
                    </span>
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener, useEventHub, useYardSpotSelect } from '@/composables';
import { useWarehouseStore } from '@/store/useWarehouseStore';

import type { ComputedRef, Ref } from 'vue';
import type { ISpot } from '@nova/core';

const SPOT_EVENTS = {
  CREATE: 'create-Spot',
  DELETE: 'delete-Spot',
  UPDATE: 'update-Spot'
} as const;

type SpotItem = {
  code: string;
  id: string;
  icon: string;
  group: string;
  status?: string;
  disabled?: boolean;
  raw?: ISpot;
};

type HeaderItem = {
  header: string;
};

type SpotListItem = SpotItem | HeaderItem;

export type IYardSpotSelect = {
  spotsByAreaRef: ComputedRef<SpotListItem[]>;
  isFetchingRef: Ref<boolean>;
  hasNoSpotsRef: ComputedRef<boolean>;
  fetchAll: () => void;
};

const { t } = useI18n();
const search = ref('');
const showDisabled = ref(true);
const warehouseStore = useWarehouseStore();
const eventHub = useEventHub();
const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-select-spot-dialog');

const yardSpot = useYardSpotSelect({
  warehouseId: warehouseStore.selectedWarehouseId,
  search: () => search.value,
  showDisabled: () => showDisabled.value,
  includeClosedSpots: () => dialogProps.value?.includeClosedSpots ?? false,
  includeAssignedSpots: () => dialogProps.value?.includeAssignedSpots ?? false,
  includeReservedSpots: () => dialogProps.value?.includeReservedSpots ?? false,
  includeAssociatedToActiveMoveTasks: () =>
    dialogProps.value?.includeAssociatedToActiveMoveTasks ?? false,
  assetType: () => dialogProps.value?.assetType ?? null
});
const isOpen = computed(() => isDialogOpen.value);
const isFetching = computed(() => yardSpot.isFetchingRef.value);
const hasNoSpots = computed(() => yardSpot.hasNoSpotsRef?.value ?? false);
const spotsByArea = computed(() => {
  const spots = yardSpot.spotsByAreaRef?.value || [];
  return spots as SpotListItem[];
});

const isHeaderItem = (item: SpotListItem): item is HeaderItem => {
  return 'header' in item;
};

const handleSpotClick = (item: SpotItem) => {
  if (!item || item.disabled || !item.raw) {
    return;
  }

  dialogProps.value?.onSpotSelected?.(item.raw);
  closeDialog();
};

watch(
  () => isDialogOpen.value,
  newVal => {
    if (newVal) {
      yardSpot.fetchAll();
    }
  }
);

const handleSpotChange = () => {
  yardSpot.fetchAll();
};

onMounted(() => {
  subscribeToDialogEvent();

  eventHub.on(SPOT_EVENTS.CREATE, handleSpotChange);
  eventHub.on(SPOT_EVENTS.DELETE, handleSpotChange);
  eventHub.on(SPOT_EVENTS.UPDATE, handleSpotChange);
});

onBeforeUnmount(() => {
  unsubscribeFromDialogEvent();

  eventHub.off(SPOT_EVENTS.CREATE, handleSpotChange);
  eventHub.off(SPOT_EVENTS.DELETE, handleSpotChange);
  eventHub.off(SPOT_EVENTS.UPDATE, handleSpotChange);
});
</script>

<style lang="scss" scoped>
:deep(.v-card) {
  height: calc(100dvh - $spacing-6) !important;
  min-height: calc(100dvh - $spacing-6) !important;
  max-height: calc(100dvh - $spacing-6) !important;
  top: $spacing-6;
  margin: 0;
  display: flex;
  flex-direction: column;
}

:deep(.v-card__title) {
  flex-shrink: 0;
}

:deep(.v-card__text) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
